import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  PlusIcon, 
  TrashIcon, 
  EyeIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  HomeIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/forms/Buttons/Button';

// Presentation-only class tokens
const navLinkCls =
  'inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';
const controlCls =
  'block w-full rounded-lg border-slate-300 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15';
const thCls = 'px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500';
const thSortCls = `${thCls} cursor-pointer select-none transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700`;
const pageBtnCls =
  'inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg px-3 text-sm font-medium transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50';
const pageBtnIdleCls = 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 enabled:hover:bg-slate-50';

const ExamsList = ({ 
  exams = [],
  onDeleteExam, 
  onCreateExam
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const safeExams = Array.isArray(exams) ? exams : [];

  const categories = ['all', ...new Set(
    safeExams.map(e => e.category || 'Без категории')
  )];

  const filteredAndSortedExams = useMemo(() => {
    let filtered = safeExams.filter(exam => {
      const matchesSearch =
        exam.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.code?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        categoryFilter === 'all' ||
        (exam.category || 'Без категории') === categoryFilter;

      return matchesSearch && matchesCategory;
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;

      if (sortField === 'created') {
        aValue = new Date(a.created || 0);
        bValue = new Date(b.created || 0);
      } else {
        aValue = a[sortField]?.toString().toLowerCase() || '';
        bValue = b[sortField]?.toString().toLowerCase() || '';
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [safeExams, searchTerm, categoryFilter, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedExams.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedExams = filteredAndSortedExams.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return '—';
    }
  };

  return (
    <div className="w-full motion-safe:animate-fade-in">
      {/* Навигационное меню */}
      <nav className="mb-6 max-w-full overflow-x-auto">
        <div className="inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1">
          <Link
            to="/"
            className={`${navLinkCls} text-slate-600 hover:text-slate-900`}
          >
            <HomeIcon className="h-4 w-4" aria-hidden="true" />
            Главная
          </Link>
          <Link
            to="/exams"
            className={`${navLinkCls} bg-white text-slate-900 shadow-sm`}
          >
            <AcademicCapIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />
            Экзамены
          </Link>
          <Link
            to="/tests"
            className={`${navLinkCls} text-slate-600 hover:text-slate-900`}
          >
            <BookOpenIcon className="h-4 w-4" aria-hidden="true" />
            Тесты
          </Link>
          <Link
            to="/questions"
            className={`${navLinkCls} text-slate-600 hover:text-slate-900`}
          >
            <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" />
            Вопросы
          </Link>
        </div>
      </nav>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Управление экзаменами</h1>
          <p className="mt-1 text-sm text-slate-500">Полный список экзаменов в системе</p>
        </div>
        <Button
          color="primary"
          onClick={onCreateExam}
          className="shrink-0 text-sm"
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          Создать экзамен
        </Button>
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <div className="min-w-0 sm:min-w-[18rem] sm:flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Поиск экзаменов..."
                className={`${controlCls} h-10 pl-10 pr-4`}
              />
            </div>
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={`${controlCls} h-10 py-0 pl-3 pr-10 sm:w-auto`}
          >
            <option value="all">Все категории</option>
            {categories.filter(c => c !== 'all').map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[56rem]">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th
                  className={`${thSortCls} text-left`}
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Название
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 text-blue-600" /> : <ChevronDownIcon className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                </th>
                <th className={`${thCls} text-left`}>Код</th>
                <th className={`${thCls} text-left`}>Категория</th>
                <th className={`${thCls} text-center`}>Тестов</th>
                <th className={`${thCls} text-center`}>Длительность</th>
                <th
                  className={`${thSortCls} text-center`}
                  onClick={() => handleSort('created')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Создан
                    {sortField === 'created' && (
                      sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 text-blue-600" /> : <ChevronDownIcon className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                </th>
                <th className={`${thCls} text-right`}>Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {paginatedExams.map((exam) => (
                <tr key={exam.uuid} className="transition-colors duration-200 hover:bg-slate-50/80">
                  <td className="px-6 py-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900">{exam.name}</div>
                      {exam.description && (
                        <div className="mt-0.5 max-w-xs truncate text-sm text-slate-500">{exam.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-700">
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700 ring-1 ring-inset ring-slate-200">
                      {exam.code || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">{exam.category || '—'}</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-700">
                    <div className="flex flex-col items-center">
                      <span className="font-medium tabular-nums text-slate-900">{exam.tests_count || 0}</span>
                      <span className="text-xs text-slate-500">тестов</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-slate-700">
                    <div className="flex flex-col items-center">
                      <span className="font-medium tabular-nums text-slate-900">{exam.duration_minutes || 0}</span>
                      <span className="text-xs text-slate-500">мин</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm tabular-nums text-slate-500">
                    {formatDate(exam.created)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => navigate(`/exams/${exam.uuid}`)}
                        className="rounded-lg p-2 text-slate-400 transition duration-200 hover:bg-blue-50 hover:text-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        title="Просмотреть экзамен"
                        aria-label="Просмотреть экзамен"
                      >
                        <EyeIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => onDeleteExam(exam.uuid)}
                        className="rounded-lg p-2 text-slate-400 transition duration-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                        title="Удалить экзамен"
                        aria-label="Удалить экзамен"
                      >
                        <TrashIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-600">
              Показано {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredAndSortedExams.length)} из {filteredAndSortedExams.length} экзаменов
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`${pageBtnCls} ${pageBtnIdleCls}`}
              >
                Предыдущая
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`${pageBtnCls} ${
                    currentPage === i + 1
                      ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/25'
                      : pageBtnIdleCls
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`${pageBtnCls} ${pageBtnIdleCls}`}
              >
                Следующая
              </button>
            </div>
          </div>
        )}
      </div>

      {filteredAndSortedExams.length === 0 && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <AcademicCapIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-medium text-slate-900">
            {safeExams.length === 0 ? 'Экзамены не загружены' : 'Экзамены не найдены'}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {safeExams.length === 0 
              ? 'Создайте первый экзамен для начала работы' 
              : 'Попробуйте изменить параметры поиска'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default ExamsList;